import classNames from 'classnames';
import type { Stage as KonvaStage } from 'konva/lib/Stage';
import React, { useRef, useState, useCallback } from 'react';

import {
  UserRewindMovieFragmentFragment,
  UserRewindSeriesFragmentFragment
} from 'types/graphql-api.generated';

import { KonvaImagePromisesProvider } from 'common/components/RewindModal/Konvas/KonvaImagePromisesProvider';
import ShareableScreenComparison from 'common/components/RewindModal/Konvas/ShareableScreenComparison';
import { shardUrl } from 'common/tools/network/url';

import Slides from '../Slides';
import styles from '../styles.module.scss';
import BottomActions from './component/BottomActions';
import Card from './component/Card';

type ScreenComparisonProps = {
  data: {
    message_1?: string;
    message_2?: string;
    image_name?: string;
  };
  entities: any[];
  onNext: () => void;
  isLastStep: boolean;
  trackingScreenName?: string;
};

const ScreenComparisonContent = ({
  data,
  entities,
  onNext,
  isLastStep,
  isDownloadReady
}: ScreenComparisonProps & { isDownloadReady: boolean }) => {
  const stageRef = useRef<KonvaStage>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const message_1 = data?.message_1 ?? 'Message de comparaison 1 non trouvé.';
  const message_2 = data?.message_2 ?? 'Message de comparaison 2 non trouvé.';
  const contentEntity = entities?.[0]?.entities?.[0] as
    | UserRewindMovieFragmentFragment
    | UserRewindSeriesFragmentFragment
    | undefined;

  const badgeUrl = data?.image_name
    ? shardUrl(`/commons/v9/allocine/flashback/${data.image_name}.png`)
    : null;

  const isSeries = contentEntity?.__typename === 'Series';

  const handleDownload = useCallback(async () => {
    if (!isDownloadReady || isDownloading || !stageRef.current) {
      return;
    }

    setIsDownloading(true);

    setTimeout(() => {
      try {
        const stage = stageRef.current;
        if (!stage) {
          throw new Error('Konva stage is not available.');
        }

        const dataURL = stage.toDataURL({ pixelRatio: 2 });

        const link = document.createElement('a');
        link.download = 'ma-comparaison-allocine-2025.png';
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        // Error handling for image generation
      } finally {
        setIsDownloading(false);
      }
    }, 100);
  }, [isDownloadReady, isDownloading]);

  return (
    <>
      <Slides>
        <div
          className={classNames(styles.contentLayout, styles.centeredLayout)}
        >
          <div className={styles.titleWrapper}>
            <h3 className={styles.slideTitle}>{message_1}</h3>
            <h3 className={styles.slideText}>{message_2}</h3>
          </div>
          <div className={styles.comparisonContainer}>
            <div className={styles.comparisonBadgeWrapper}>
              {badgeUrl && (
                <img
                  src={badgeUrl}
                  alt="Badge de comparaison"
                  className={styles.comparisonBadge}
                />
              )}
            </div>

            <Card path={contentEntity?.poster?.path} />
          </div>
        </div>

        <BottomActions
          showShare={true}
          onShareClick={handleDownload}
          isShareDisabled={!isDownloadReady || isDownloading}
          shareText={isDownloading ? 'Génération...' : "Partager cette stat'"}
          showNext={!isLastStep}
          onNextClick={onNext}
        />
      </Slides>

      <ShareableScreenComparison
        ref={stageRef}
        data={data}
        entities={entities}
        isSeries={isSeries}
      />
    </>
  );
};

const ScreenComparison = (props: ScreenComparisonProps) => {
  const [isDownloadReady, setIsDownloadReady] = useState(false);

  return (
    <KonvaImagePromisesProvider onDownloadableChange={setIsDownloadReady}>
      <ScreenComparisonContent {...props} isDownloadReady={isDownloadReady} />
    </KonvaImagePromisesProvider>
  );
};

export default ScreenComparison;
